import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, ErrorBox, LoadingRows } from "@/components/common";
import { fmtDateTime } from "@/lib/format";
import { Mail, CalendarDays, Link2, Unlink, Copy, ShieldCheck, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export function MailboxSettings() {
  const { me, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [mine, setMine] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", tenant: "common" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([api.get("/integrations/m365/config"), api.get("/integrations/m365/me")]);
      setCfg(c.data); setMine(m.data);
      setForm((f) => ({ ...f, client_id: c.data.client_id || "", tenant: c.data.tenant || "common" }));
      setError(null);
    } catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const st = params.get("m365");
    if (!st) return;
    if (st === "connected") toast.success("Microsoft 365 mailbox connected");
    else toast.error(params.get("detail") || "Microsoft 365 connection failed");
    const next = new URLSearchParams(params); next.delete("m365"); next.delete("detail"); next.delete("reason");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/m365/connect"); window.location.assign(data.url); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.delete("/integrations/m365/me"); toast.success("Mailbox disconnected"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const syncCal = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/m365/calendar/sync"); toast.success(`${data.synced} meeting${data.synced === 1 ? "" : "s"} synced into your Planner`); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveCfg = async () => {
    setBusy(true); setError(null);
    try {
      await api.put("/integrations/m365/config", { client_id: form.client_id.trim(), client_secret: form.client_secret.trim() || null, tenant: form.tenant.trim() || "common" });
      toast.success("Microsoft 365 app details saved"); setForm((f) => ({ ...f, client_secret: "" })); await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const removeCfg = async () => {
    if (!window.confirm("Remove the Microsoft 365 configuration? Every member's mailbox connection will be disconnected.")) return;
    setBusy(true);
    try { await api.delete("/integrations/m365/config"); toast.success("Configuration removed"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

  if (!cfg || !mine) return error ? <ErrorBox error={error} onRetry={load} /> : <LoadingRows rows={4} />;
  const conn = mine.connection;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="mailbox-settings">
      {error && <ErrorBox error={error} />}

      <section className="panel p-4 space-y-3" data-testid="mailbox-connection">
        <div className="flex items-center justify-between">
          <h3 className="section-title flex items-center gap-2"><Mail className="h-4 w-4" /> Your Microsoft 365 mailbox & calendar</h3>
          <span className={`chip ${conn?.status === "connected" ? "chip-success" : conn?.status === "reauth_required" ? "chip-warning" : "chip-muted"}`} data-testid="mailbox-status">
            {conn?.status === "connected" ? "Connected" : conn?.status === "reauth_required" ? "Reconnect needed" : "Not connected"}
          </span>
        </div>
        <p className="meta">Emails you approve are sent from your own Outlook mailbox and saved to Sent Items. Your Outlook meetings appear in the Planner as protected time, and meetings you schedule here are created in your calendar. VoroSuite never stores your password - access can be revoked here or in Microsoft at any time.</p>
        {!cfg.configured && <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="mailbox-not-configured">Your organisation has not configured Microsoft 365 yet. {cfg.can_manage ? "Add the Azure app details below." : "Ask an administrator to add the Azure app details in Settings."}</div>}
        {conn && (
          <div className="text-sm space-y-1">
            <div><span className="font-medium">{conn.display_name || conn.email}</span> <span className="meta">{conn.email}</span></div>
            <div className="meta">Connected {fmtDateTime(conn.connected_at)}{conn.last_used_at ? ` · last used ${fmtDateTime(conn.last_used_at)}` : ""}{conn.calendar_synced_at ? ` · calendar synced ${fmtDateTime(conn.calendar_synced_at)}` : ""}</div>
            {conn.last_error && <div className="text-xs text-destructive">{conn.last_error}</div>}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {(!conn || conn.status !== "connected") && <Button onClick={connect} disabled={busy || !cfg.configured} data-testid="mailbox-connect-button"><Link2 className="h-4 w-4 mr-1" />{conn?.status === "reauth_required" ? "Reconnect Microsoft 365" : "Connect Microsoft 365"}</Button>}
          {conn?.status === "connected" && <>
            <Button variant="outline" onClick={syncCal} disabled={busy} data-testid="mailbox-sync-calendar"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />Sync calendar now</Button>
            <Button variant="ghost" onClick={disconnect} disabled={busy} data-testid="mailbox-disconnect-button"><Unlink className="h-4 w-4 mr-1" />Disconnect</Button>
          </>}
        </div>
        <div className="grid sm:grid-cols-3 gap-2 text-xs pt-1">
          <div className="rounded-md border border-border p-2"><div className="font-medium flex items-center gap-1"><Mail className="h-3.5 w-3.5" />Send email</div><div className="meta">Only after you review and click Send</div></div>
          <div className="rounded-md border border-border p-2"><div className="font-medium flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />Calendar</div><div className="meta">Read meetings; create invitations</div></div>
          <div className="rounded-md border border-border p-2"><div className="font-medium flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Replies</div><div className="meta">Reads replies to emails you sent, nothing else</div></div>
        </div>
      </section>

      {cfg.can_manage && (
        <section className="panel p-4 space-y-3" data-testid="m365-admin-config">
          <div className="flex items-center justify-between">
            <h3 className="section-title">Organisation: Microsoft Entra app registration</h3>
            <span className={`chip ${cfg.configured ? "chip-success" : "chip-muted"}`} data-testid="m365-config-status">{cfg.configured ? `Configured · ${cfg.connected_users} connected` : "Not configured"}</span>
          </div>
          <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-0.5">
            <li>In the <a className="text-primary underline inline-flex items-center gap-0.5" href="https://entra.microsoft.com" target="_blank" rel="noopener noreferrer">Microsoft Entra admin center<ExternalLink className="h-3 w-3" /></a>, register an app for <b>Accounts in any organizational directory</b> (or your directory only).</li>
            <li>Authentication → Add platform <b>Web</b> with exactly this redirect URI.</li>
            <li>Certificates & secrets → New client secret. Paste the secret <b>value</b> below (shown once).</li>
            <li>API permissions → Microsoft Graph → Delegated: {cfg.scopes.filter((s) => !["openid", "profile", "email"].includes(s)).join(", ")}. Grant admin consent if your tenant requires it.</li>
          </ol>
          <Field label="Redirect URI (copy into Azure)">
            <div className="flex gap-2"><Input readOnly value={cfg.redirect_uri} className="font-mono text-xs" data-testid="m365-redirect-uri" /><Button variant="outline" size="icon" onClick={() => copy(cfg.redirect_uri)} aria-label="copy redirect uri"><Copy className="h-4 w-4" /></Button></div>
          </Field>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Application (client) ID"><Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" className="font-mono text-xs" data-testid="m365-client-id" /></Field>
            <Field label={cfg.configured ? `Client secret (leave blank to keep ${cfg.secret_hint || "current"})` : "Client secret value"}><Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} autoComplete="off" data-testid="m365-client-secret" /></Field>
            <Field label="Tenant" hint="'common' for any work account, or your directory ID"><Input value={form.tenant} onChange={(e) => setForm({ ...form, tenant: e.target.value })} className="font-mono text-xs" data-testid="m365-tenant" /></Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveCfg} disabled={busy || form.client_id.length < 8 || (!cfg.configured && form.client_secret.length < 8)} data-testid="m365-save-config">{cfg.configured ? "Update app details" : "Save app details"}</Button>
            {cfg.configured && <Button variant="ghost" onClick={removeCfg} disabled={busy} data-testid="m365-remove-config">Remove configuration</Button>}
          </div>
          {cfg.updated_at && <div className="meta">Last updated {fmtDateTime(cfg.updated_at)}. The secret is stored encrypted and never shown again.</div>}
          {cfg.members?.length > 0 && (
            <div className="pt-2">
              <div className="text-xs font-medium mb-1">Connected members</div>
              <ul className="divide-y divide-border text-sm" data-testid="m365-members">{cfg.members.map((m) => <li key={m.id || m.user_id} className="py-1.5 flex items-center justify-between gap-2"><span>{m.user_name || m.user_email} <span className="meta">→ {m.email}</span></span><span className={`chip ${m.status === "connected" ? "chip-success" : "chip-warning"}`}>{m.status === "connected" ? "Connected" : "Reconnect needed"}</span></li>)}</ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
